// apps/web/src/components/analytics/decide/PeerBenchmarkPanel.tsx
//
// P8a Decide tab — sector activity vs global feed (last 7 days). Empty
// state when no sector preferences. Otherwise expand-inline (no nav) to
// reveal a per-sector breakdown with delta vs the global mean. Uses the
// shared formatDelta + displayMapper primitives shipped by P7.

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAnalyticsDecidePeerBenchmark } from '@/hooks/useAnalyticsDecidePeerBenchmark'
import { displayMapper } from '@/lib/display-mapper'
import { formatDelta } from '@/lib/delta-format'

export interface PeerBenchmarkPanelProps {
  sectors: string[]
}

const COLOUR_CLASS: Record<'red' | 'green' | 'neutral', string> = {
  red: 'text-red-600 dark:text-red-400',
  green: 'text-green-600 dark:text-green-400',
  neutral: 'text-muted-foreground',
}

export function PeerBenchmarkPanel({ sectors }: PeerBenchmarkPanelProps) {
  const [expanded, setExpanded] = useState(false)

  if (sectors.length === 0) {
    return (
      <section className="rounded-md border border-border p-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Peer benchmark
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Add sector preferences to compare your sectors against the global feed.{' '}
          <Link to="/settings#preferences" className="underline">
            Open preferences
          </Link>
          .
        </p>
      </section>
    )
  }

  return (
    <PeerBenchmarkBody
      sectors={sectors}
      expanded={expanded}
      onToggle={() => setExpanded((v) => !v)}
    />
  )
}

interface BodyProps {
  sectors: string[]
  expanded: boolean
  onToggle: () => void
}

function PeerBenchmarkBody({ sectors, expanded, onToggle }: BodyProps) {
  const { data, isLoading } = useAnalyticsDecidePeerBenchmark(sectors)

  return (
    <section className="rounded-md border border-border p-4">
      <header className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Peer benchmark
        </h2>
        <button
          type="button"
          onClick={onToggle}
          className="text-xs underline text-muted-foreground"
        >
          {expanded ? 'Collapse' : 'Show breakdown'}
        </button>
      </header>
      {!isLoading && expanded && data?.sectors && data.sectors.length > 0 && (
        <ul className="mt-3 space-y-1 text-sm">
          {data.sectors.map((s) => {
            const fmt = formatDelta({
              value: s.deltaPct,
              valuePolarity: 'bad-up',
            })
            const pct = Math.abs(Math.round(s.deltaPct * 100))
            return (
              <li
                key={s.sectorSlug}
                className="flex items-center justify-between gap-3"
              >
                <span>{displayMapper.sector(s.sectorSlug)}</span>
                <span className={COLOUR_CLASS[fmt.colour]}>
                  {fmt.arrow} {fmt.sign}
                  {pct}%
                </span>
              </li>
            )
          })}
        </ul>
      )}
      {!isLoading && expanded && (!data?.sectors || data.sectors.length === 0) && (
        <p className="mt-3 text-sm text-muted-foreground">
          No leak-site activity for these sectors in the last 7 days.
        </p>
      )}
    </section>
  )
}
